import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { isWithinInterval, parseISO, startOfDay, endOfDay, isSameDay, isValid } from 'date-fns';

const safeParse = (val) => {
    if (!val) return null;
    if (val instanceof Date) return isValid(val) ? val : null;
    if (typeof val === 'string') {
        try {
            return parseISO(val.replace(' ', 'T'));
        } catch (e) {
            return null;
        }
    }
    return null;
};

/**
 * useLeaderExport - Zentraler Hook für die Filter- und Selektionslogik des PDF-Exports.
 * 
 * @param {Object} props
 * @param {Array} props.assignments - Alle verfügbaren Einsätze
 * @param {Array} props.workers - Alle Mitarbeiter
 * @param {Array} props.vehicles - Alle Fahrzeuge
 * @param {Array} props.kolonnen - Alle Kolonnen-Definitionen
 * @param {boolean} props.isReadOnly - Rank 2 Modus (persönliche Daten)
 * @param {string|number} props.workerId - ID des aktuellen Mitarbeiters (für Rank 2 Filter)
 * @param {Array} props.initialSelectedIds - Vorbelegte Auswahl (für Deep-Links)
 */
export function useLeaderExport({ 
    assignments = [], 
    workers = [], 
    vehicles = [], 
    kolonnen = [],
    isReadOnly = false,
    workerId = null,
    initialSelectedIds = [],
    initialFilters = null
}) {
    // --- Filter State ---
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [statusFilter, setStatusFilter] = useState(['aktiv', 'pausiert', 'abgeschlossen']);
    const [workerFilter, setWorkerFilter] = useState([]);
    const [vehicleFilter, setVehicleFilter] = useState([]);
    const [kolonnenFilter, setKolonnenFilter] = useState([]);
    const [dateMode, setDateMode] = useState('calendar'); // 'calendar', 'undated', 'combined'
    const [sortOrder, setSortOrder] = useState('date_desc'); // date_desc, date_asc, name_asc, name_desc

    // --- UI State ---
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [openDropdown, setOpenDropdown] = useState(null); // 'status' | 'worker' | 'vehicle' | 'kolonne'

    // --- Selection State ---
    const [selectedAssignmentIds, setSelectedAssignmentIds] = useState(new Set(initialSelectedIds));
    const [mockAssignments, setMockAssignments] = useState([]);
    const filtersAppliedRef = useRef(false);

    // Effektive Assignments (Real + Mock)
    const effectiveAssignments = useMemo(() => [...assignments, ...mockAssignments], [assignments, mockAssignments]);

    /**
     * Erzeugt oder entfernt 1000 Mock-Einsätze für Performance-Tests (Toggle).
     */
    const injectMockData = useCallback(() => {
        if (mockAssignments.length > 0) {
            // Mock-Daten entfernen
            setMockAssignments([]);
            setSelectedAssignmentIds(prev => {
                const next = new Set(prev);
                mockAssignments.forEach(m => next.delete(String(m.id)));
                return next;
            });
            return;
        }

        const mocks = Array.from({ length: 1000 }).map((_, i) => ({
            id: `mock-${i}`,
            project_name: `Stress Test Projekt ${i + 1}`,
            location: `Teststraße ${i}, 12345 Mock-City`,
            status: ['geplant', 'aktiv', 'abgeschlossen'][i % 3],
            start_datetime: i % 5 === 0 ? null : new Date(Date.now() - (i * 3600000)).toISOString(),
            workers: [],
            vehicles: [],
            notes: `Automatischer Performance-Test Eintrag #${i}`
        }));
        
        // Filter zurücksetzen, damit Mocks sofort sichtbar sind
        setDateMode('combined');
        setDateRange({ start: null, end: null });
        setSearchTerm('');
        
        setMockAssignments(mocks);
        // Automatisch alle Mocks selektieren für maximalen PDF-Stress
        setSelectedAssignmentIds(prev => {
            const next = new Set(prev);
            mocks.forEach(m => next.add(String(m.id)));
            return next;
        });
    }, [mockAssignments]);

    // --- Dokumenten-Optionen ---
    const [options, setOptions] = useState({
        showKolonnenList: true,
        showWorkerList: true,
        showVehicleList: true,
        showNotes: true,
        showSignatures: true,
        exportStyle: 'compact'
    });

    // Initialisierung der Filter (Standard: Alle gewählt)
    const filtersInitializedRef = useRef({ workers: false, vehicles: false, kolonnen: false });

    useEffect(() => {
        if (!filtersInitializedRef.current.workers && workers.length > 0) {
            setWorkerFilter(workers.map(w => w.id));
            filtersInitializedRef.current.workers = true;
        }
        if (!filtersInitializedRef.current.vehicles && vehicles.length > 0) {
            setVehicleFilter(vehicles.map(v => v.id));
            filtersInitializedRef.current.vehicles = true;
        }
        
        // Kolonnen-Initialisierung: Wir warten auf Daten, aber setzen 'none' schon mal als Basis
        if (!filtersInitializedRef.current.kolonnen) {
            if (kolonnen.length > 0) {
                setKolonnenFilter([...kolonnen.map(k => k.id), 'none']);
                filtersInitializedRef.current.kolonnen = true;
            } else if (kolonnenFilter.length === 0 && assignments.length > 0) {
                setKolonnenFilter(['none']);
                // Wir setzen .kolonnen noch nicht auf true, falls kolonnen noch laden
            }
        }
    }, [workers, vehicles, kolonnen, assignments, kolonnenFilter.length]);

    // Wenn sich initialSelectedIds ändert (Deep Link), Auswahl aktualisieren
    useEffect(() => {
        if (initialSelectedIds.length > 0) {
            setSelectedAssignmentIds(new Set(initialSelectedIds.map(String)));
            
            // Wenn wir einen Deep-Link haben, versuchen wir den Zeitraum sinnvoll zu setzen
            const selectedAssignments = assignments.filter(a => initialSelectedIds.map(String).includes(String(a.id)));
            const hasUndated = selectedAssignments.some(a => !a.start_datetime);
            const dated = selectedAssignments.filter(a => a.start_datetime);

            if (hasUndated && dated.length === 0) {
                setDateMode('undated');
            } else if (hasUndated && dated.length > 0) {
                setDateMode('combined');
            }

            if (dated.length > 0) {
                // Wir setzen den Zeitraum auf den Tag des ersten datierten Einsatzes,
                // aber wir könnten auch den gesamten Bereich der gewählten Einsätze nehmen.
                // Für den Moment bleiben wir beim ersten Tag, aber wir sorgen dafür, dass der Kalender sichtbar ist.
                const firstA = dated[0];
                const d = firstA.start_datetime.split(' ')[0];
                setDateRange({ start: d, end: d });
            }
        }
    }, [initialSelectedIds, assignments]);

    /**
     * Filtert die Einsätze basierend auf den aktiven Filtern.
     */
    const filteredCandidates = useMemo(() => {
        return effectiveAssignments.filter(a => {
            // 0. Rank 2 Hard-Filter (Sicherheitsebene)
            if (isReadOnly && workerId) {
                const isPart = (a.workers || []).some(w => String(w.worker_id) === String(workerId));
                if (!isPart) return false;
            }

            // 1. Suche
            if (searchTerm) {
                const lower = searchTerm.toLowerCase();
                const match = (a.project_name || '').toLowerCase().includes(lower) ||
                              (a.location || '').toLowerCase().includes(lower) ||
                              (a.notes || '').toLowerCase().includes(lower);
                if (!match) return false;
            }

            // 2. Datum / DateMode Logic
            const hasDate = !!a.start_datetime;

            if (dateMode === 'undated') {
                if (hasDate) return false;
            } else {
                // combined oder calendar
                const isInRange = (() => {
                    if (!hasDate) return dateMode === 'combined';
                    
                    if (dateRange.start || dateRange.end) {
                        const aStart = safeParse(a.start_datetime);
                        const aEnd = safeParse(a.end_datetime || a.start_datetime);
                        
                        if (aStart) aStart.setHours(0,0,0,0);
                        if (aEnd) aEnd.setHours(23,59,59,999);

                        if (!aStart) return dateMode === 'combined';

                        if (dateRange.start) {
                            const parsedStart = safeParse(dateRange.start);
                            if (parsedStart) {
                                const start = startOfDay(parsedStart);
                                // Assignment overlaps if its end is NOT before our start
                                if (aEnd < start) return false;
                            }
                        }
                        if (dateRange.end) {
                            const parsedEnd = safeParse(dateRange.end);
                            if (parsedEnd) {
                                const end = endOfDay(parsedEnd);
                                // Assignment overlaps if its start is NOT after our end
                                if (aStart > end) return false;
                            }
                        }
                        return true;
                    }
                    return true; 
                })();

                if (!isInRange) return false;
            }

            // 3. Status
            if (!statusFilter.includes(a.status)) return false;

            // 4. Ressourcen (Mitarbeiter, Fahrzeuge, Kolonnen)
            // Logik: Ein Einsatz wird ausgeblendet, wenn ein Ressourcen-Filter AKTIV eingeschränkt ist
            // und der Einsatz KEINE der gewählten Ressourcen enthält.
            
            const aWorkerIds = (a.workers || []).map(w => w.worker_id);
            const aVehicleIds = (a.vehicles || []).map(v => v.vehicle_id);
            const aKolonnenIds = [...new Set([
                ...(a.workers || []).map(w => String(w.kolonne_id)),
                ...(a.vehicles || []).map(v => String(v.kolonne_id))
            ])].filter(Boolean);

            const isWorkerFiltered = workerFilter.length > 0 && workerFilter.length < workers.length;
            const isVehicleFiltered = vehicleFilter.length > 0 && vehicleFilter.length < vehicles.length;
            const totalKolonnenOptions = kolonnen.length + 1;
            const isKolonnenFiltered = kolonnenFilter.length > 0 && kolonnenFilter.length < totalKolonnenOptions;

            // Logik: Ein Einsatz wird ausgeblendet, wenn ein Ressourcen-Filter AKTIV eingeschränkt ist
            // und der Einsatz KEINE der gewählten Ressourcen enthält.
            
            // Wenn spezifische Filter aktiv sind, muss mindestens einer matchen
            if (isWorkerFiltered) {
                const hasMatch = aWorkerIds.some(id => workerFilter.map(String).includes(id));
                if (aWorkerIds.length > 0 && !hasMatch) return false;
            }
            if (isVehicleFiltered) {
                const hasMatch = aVehicleIds.some(id => vehicleFilter.map(String).includes(id));
                if (aVehicleIds.length > 0 && !hasMatch) return false;
            }
            if (isKolonnenFiltered) {
                if (aKolonnenIds.length === 0) {
                    // Wenn keine Kolonne zugeordnet ist, muss 'none' im Filter sein
                    if (!kolonnenFilter.includes('none')) return false;
                } else {
                    const hasMatch = aKolonnenIds.some(id => kolonnenFilter.includes(String(id)));
                    if (!hasMatch) return false;
                }
            }

            return true;
        });
    }, [effectiveAssignments, searchTerm, dateRange, statusFilter, workerFilter, vehicleFilter, kolonnenFilter, isReadOnly, workerId, workers, vehicles, kolonnen, dateMode]);

    /**
     * Die finalen Einsätze für das PDF (gefiltert UND manuell gewählt).
     */
    const finalAssignments = useMemo(() => {
        const items = filteredCandidates.filter(a => selectedAssignmentIds.has(String(a.id)));
        
        // Sorting logic
        items.sort((a, b) => {
            if (sortOrder.startsWith('date')) {
                const dateA = new Date(a.start_datetime || 0);
                const dateB = new Date(b.start_datetime || 0);
                return sortOrder === 'date_asc' ? dateA - dateB : dateB - dateA;
            } else {
                const nameA = (a.project_name || '').toLowerCase();
                const nameB = (b.project_name || '').toLowerCase();
                if (nameA < nameB) return sortOrder === 'name_asc' ? -1 : 1;
                if (nameA > nameB) return sortOrder === 'name_asc' ? 1 : -1;
                return 0;
            }
        });

        return items;
    }, [filteredCandidates, selectedAssignmentIds, sortOrder]);

    // --- Hilfsfunktionen für die UI ---

    const toggleSection = useCallback((section) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) next.delete(section);
            else next.add(section);
            return next;
        });
    }, []);

    const toggleAssignment = useCallback((id) => {
        const idStr = String(id);
        setSelectedAssignmentIds(prev => {
            const next = new Set(prev);
            if (next.has(idStr)) next.delete(idStr);
            else next.add(idStr);
            return next;
        });
    }, []);

    const selectAllCandidates = useCallback(() => {
        setSelectedAssignmentIds(new Set(filteredCandidates.map(a => String(a.id))));
    }, [filteredCandidates]);

    const deselectAllCandidates = useCallback(() => {
        setSelectedAssignmentIds(new Set());
    }, []);

    useEffect(() => {
        if (!initialFilters) {
            filtersAppliedRef.current = false;
            return;
        }

        if (initialFilters && !filtersAppliedRef.current) {
            if (initialFilters.status) setStatusFilter(initialFilters.status);
            if (initialFilters.dateRange) setDateRange(initialFilters.dateRange);
            if (initialFilters.worker) setWorkerFilter(initialFilters.worker);
            if (initialFilters.vehicle) setVehicleFilter(initialFilters.vehicle);
            if (initialFilters.kolonne) setKolonnenFilter(initialFilters.kolonne);
            if (initialFilters.search !== undefined) setSearchTerm(initialFilters.search);
            if (initialFilters.dateMode) setDateMode(initialFilters.dateMode);
            else setDateMode('combined');
            
            filtersAppliedRef.current = true;
        }
    }, [initialFilters]);

    // Automatische Selektion nach dem Anwenden von Initialfiltern
    useEffect(() => {
        if (filtersAppliedRef.current && filteredCandidates.length > 0) {
            if (initialSelectedIds && initialSelectedIds.length > 0) {
                // Bei Bulk-Auswahl nur die exakt gewählten IDs übernehmen
                setSelectedAssignmentIds(new Set(initialSelectedIds.map(id => String(id))));
            } else {
                // Bei normalem Öffnen alle sichtbaren Kandidaten wählen
                setSelectedAssignmentIds(new Set(filteredCandidates.map(a => String(a.id))));
            }
            filtersAppliedRef.current = false;
        }
    }, [filteredCandidates, initialSelectedIds]);

    const toggleOption = useCallback((key, value) => {
        setOptions(prev => ({ ...prev, [key]: value !== undefined ? value : !prev[key] }));
    }, []);

    const resetAll = useCallback(() => {
        setSearchTerm('');
        setDateRange({ start: null, end: null });
        setDateMode('combined');
        setStatusFilter(['aktiv', 'pausiert', 'abgeschlossen']);
        setWorkerFilter(workers.map(w => w.id));
        setVehicleFilter(vehicles.map(v => v.id));
        setKolonnenFilter([...kolonnen.map(k => k.id), 'none']);
        setOptions({
            showKolonnenList: true,
            showWorkerList: true,
            showVehicleList: true,
            showNotes: true,
            showSignatures: true,
            exportStyle: 'compact'
        });
        setSortOrder('date_desc');
        setSelectedAssignmentIds(new Set());
    }, [workers, vehicles, kolonnen]);

    return {
        // State
        searchTerm, setSearchTerm,
        dateRange, setDateRange,
        statusFilter, setStatusFilter,
        workerFilter, setWorkerFilter,
        vehicleFilter, setVehicleFilter,
        kolonnenFilter, setKolonnenFilter,
        selectedAssignmentIds,
        expandedSections, toggleSection,
        openDropdown, setOpenDropdown,
        options, toggleOption,
        sortOrder, setSortOrder,
        dateMode, setDateMode,

        // Data
        filteredCandidates,
        finalAssignments,
        effectiveAssignments,

        // Actions
        toggleAssignment,
        selectAllCandidates,
        deselectAllCandidates,
        injectMockData,
        resetAll
    };
}
